import {useCallback, useContext, useState} from "react";
import AuthContext from "../store/auth-context";

export interface RequestConfig {
    url: string
    method ?: string
    body ?: string
    dontAddAuth ?: boolean
    headers ?: {name: string, value: string}[]
}

const useCallBackend = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const authCtx = useContext(AuthContext);

    const invokeBackend = useCallback((requestConfig:RequestConfig, handleResponse:(response:any) => void):void => {
        setIsLoading(true);
        setError(null);

        try {
            const uri = process.env.REACT_APP_BACKEND_URI === undefined ? "" : process.env.REACT_APP_BACKEND_URI;

            if(!uri){
                throw new Error("Could not get backend uri.")
            }

            const headers = new Headers();
            if(!requestConfig.dontAddAuth){
                const sigToken = authCtx.token;
                if(sigToken === null) {
                    throw new Error("Need to re-authenticate.");
                }
                headers.append("Auth", `Signio ${sigToken}`);
            }
            if(requestConfig.method === "POST"){
                headers.append("Content-Type", "application/json");
            }
            if(requestConfig.headers !== null) {
                requestConfig.headers?.forEach(header => {
                    headers.append(header.name, header.value);
                })
            }

            const requestOptions = {
                method: requestConfig.method ? requestConfig.method : "GET",
                headers: headers,
                body: requestConfig.body ? requestConfig.body : null
            }

            fetch(uri + requestConfig.url, requestOptions)
                .then(response => {

                    if(response.status === 401) {
                        setError("401 - Unauthorized")
                    } else if(response.status === 400){
                        setError("Please confirm all information is provided and it is in the correct format.")
                    } else if(response.ok) {
                        response.json().then(data => {
                            handleResponse(data);
                        }).catch(e => {
                            setError(e.message);
                        });
                    } else {
                        setError("Code - " + response.status);
                    }

                    setIsLoading(false);
                })
                .catch(e => {
                    setError(e.message);
                    setIsLoading(false);
                });
        } catch (err:any){
            setError(err.message || "There was an error!");
            setIsLoading(false);
        }

    }, [authCtx.token]);

    return {
        isLoading,
        error,
        invokeBackend
    }

}

export default useCallBackend;